/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { differenceInCalendarDays, format, isSameDay } from 'date-fns';
import { KEYS, TAGS } from 'modules/dashboard/constants';
import { nameFilter } from 'modules/dashboard/functions';
import {
  BOOKING_DATE_START_DATE,
  YEAR_MONTH_DAY_PATTERN,
} from 'modules/common/constants/date-range';
import calculateComparisonDates, {
  getWeeklyAlignedCustomDate,
} from 'modules/common/utils/dateComparisonUtils';
import { compareWindows } from 'modules/common/ui-controller';
import { ENVIRONMENT } from 'config';
import { faro } from '@grafana/faro-web-sdk';
import faroLog from 'modules/common/utils/faro';
import {
  ALIGNMENT_FILTER,
  BOOKING_DATE_COMPARISON,
  BOOKING_DATE_LOWER,
  BOOKING_DATE_RANGE,
  BOOKING_DATE_UPPER,
  BREAKDOWN_LIST_FILTER,
  BREAKDOWN_MAP,
  BREAKDOWN_SCHEMA_FILTER,
  COLUMN_DYNAMIC_BREAKDOWN,
  COLUMN_DYNAMIC_MEASURE,
  COLUMNS,
  COMPARE_BOOKING_DATE_LOWER,
  COMPARE_BOOKING_DATE_UPPER,
  COMPARE_BOOKING_OTB_DATE_LOWER,
  COMPARE_BOOKING_OTB_DATE_UPPER,
  COMPARE_STAY_DATE_LOWER,
  COMPARE_STAY_DATE_UPPER,
  COMPARE_WINDOW_MAP,
  COMPARISON_WINDOW_FILTER,
  CUMULATIVE_COLUMNS,
  CURRENCY_COMPARISON,
  CURRENCY_FILTER,
  FILE_NAMES,
  FORECAST_COLUMNS,
  FORECAST_CUMULATIVE_COLUMNS,
  GRANULARITY,
  GRANULARITY_FILTER,
  GROUP_A_FILTER,
  GROUP_A_NAME,
  GROUP_A_NAME_FILTER,
  GROUP_B_FILTER,
  GROUP_B_NAME,
  GROUP_B_NAME_FILTER,
  HOTEL_COLUMNS,
  HOTEL_COMP_FILTER,
  HOTEL_CUMULATIVE_COLUMNS,
  HOTEL_FILTER,
  HOTEL_FORECAST_CUMULATIVE_COLUMNS,
  HOTEL_MOVING_AVERAGE_COLUMNS,
  HOTEL_PERCENTAGE_COLUMNS,
  HOTEL_SEGMENT_CUMULATIVE_COLUMNS,
  HOTEL_SEGMENT_DAILY_COLUMNS,
  HOTEL_SEGMENT_WEEKLY_COLUMNS,
  HOTEL_W_FORECAST_COLUMNS,
  HOTEL_WEEKLY_COLUMNS,
  HOTEL_WEEKLY_FORECAST_COLUMNS,
  MOVING_AVERAGE_COLUMNS,
  MOVING_AVERAGE_FILTER,
  NORMALIZATION,
  NORMALIZED_FILTER,
  PERCENTAGE_COLUMNS,
  PMS_FILTER,
  PMS_SYNC,
  SEGMENT_CUMULATIVE_DAILY_COLUMNS,
  SEGMENT_CUMULATIVE_GROUP_DAILY_COLUMNS,
  SEGMENT_DAILY_COLUMNS,
  SEGMENT_GROUP_DAILY_COLUMNS,
  SEGMENT_GROUP_WEEKLY_COLUMNS,
  SEGMENT_MOVING_AVERAGE_GROUP_WEEKLY_COLUMNS,
  SEGMENT_MOVING_AVERAGE_WEEKLY_COLUMNS,
  SEGMENT_PERCENTAGE_GROUP_WEEKLY_COLUMNS,
  SEGMENT_PERCENTAGE_WEEKLY_COLUMNS,
  SEGMENT_WEEKLY_COLUMNS,
  SELECTED_CURRENCY,
  STAY_DATE_COMPARISON,
  STAY_DATE_LOWER,
  STAY_DATE_RANGE,
  STAY_DATE_UPPER,
  TABLE_MEASURES,
  TABULAR_CUMULATIVE_TITLE,
  TABULAR_HOTEL_CUMULATIVE_TITLE,
  TABULAR_HOTEL_MOVING_AVERAGE_TITLE,
  TABULAR_HOTEL_PERCENTAGE_TITLE,
  TABULAR_HOTEL_TITLE,
  TABULAR_HOTEL_WEEKLY_TITLE,
  TABULAR_MOVING_AVERAGE_TITLE,
  TABULAR_PERCENTAGE_TITLE,
  TABULAR_SEGMENT_CUMULATIVE_TITLE,
  TABULAR_SEGMENT_MOVING_AVERAGE_TITLE,
  TABULAR_SEGMENT_PERCENTAGE_TITLE,
  TABULAR_SEGMENT_TIME_CUMULATIVE_HOTEL_TITLE,
  TABULAR_SEGMENT_TIME_CUMULATIVE_TITLE,
  TABULAR_SEGMENT_TIME_HOTEL_TITLE,
  TABULAR_SEGMENT_TIME_TITLE,
  TABULAR_SEGMENT_TIME_WEEKLY_HOTEL_TITLE,
  TABULAR_SEGMENT_TIME_WEEKLY_TITLE,
  TABULAR_SEGMENT_TITLE,
  TABULAR_SEGMENT_WEEKLY_TITLE,
  TABULAR_TITLE,
  TABULAR_WEEKLY_TITLE,
  TIME_SEGMENT_CUMULATIVE_COLUMNS,
  TIME_SEGMENT_DAILY_COLUMNS,
  TIME_SEGMENT_WEEKLY_COLUMNS,
  TREND_GRANULARITY_COLUMN_MAPPER,
  WEEKLY_COLUMNS,
  WEEKLY_FORECAST_COLUMNS,
} from './constants';

/**
 * Find visual function used in trend tabs widget
 * @param {Array} array - Visual array
 * @param {string} index - Order of the visual
 * @param {Boolean} includeTag - Whether to include the tags or exclude them from final result
 * @param {Array} tags - Tags list
 * @returns
 */
export const findVisual = (array, index, includeTag, tags, percentage = false) =>
  array?.find((visual) => {
    if (!percentage && (index === 0 || index === 1 || index === 5)) {
      return !visual?.tags?.includes(...tags) && visual?.order === index;
    }
    return includeTag
      ? visual?.tags?.includes(...tags) && visual?.order === index
      : !visual?.tags?.includes(...tags) && visual?.order === index;
  });
/**
 * Determines the granularity based on the difference in calendar days between
 * the start and end dates of a target date range.
 * @param {Object} targetDate - The target date range object with 'startDate' and 'endDate'.
 * @param {Date} targetDate.startDate - The start date of the target range.
 * @param {Date} targetDate.endDate - The end date of the target range.
 * @returns {string|undefined} - The calculated granularity, or undefined if not matched.
 */

export const getGranularity = (targetDate) => {
  let granularity;
  const difference =
    differenceInCalendarDays(new Date(targetDate.endDate), new Date(targetDate.startDate)) + 1;
  //
  if (difference <= 60) {
    granularity = GRANULARITY.DAILY;
  } else if (difference < 365) {
    granularity = GRANULARITY.WEEKLY;
  } else if (difference <= 1460) {
    granularity = GRANULARITY.MONTHLY;
  } else {
    granularity = GRANULARITY.YEARLY;
  }
  return granularity;
};

/**
 * Generate power bi filter schema for breakdown and focus on filters
 * @param {Array} visualFilters - List of registered visual filters
 * @param {String} chartBreakdown  - selected breakdown values
 * @param {Boolean} isDetailed - state of detailed toggle
 * @param {Array} focusOn - selected focus on values
 * @returns
 */
export const getTSBreakdownAndFocusOnSchema = (
  visualFilters,
  chartBreakdown,
  isDetailed,
  focusOn,
  segmentGroup1,
  segmentGroup2
) => {
  const breakdownName = visualFilters?.filter((filter) => filter?.id?.includes(chartBreakdown))[0]
    ?.type;
  const breakdownVisual = visualFilters?.filter(
    (filter) => filter?.tags?.includes(TAGS.AGGREGATION) && filter?.type === breakdownName
  );
  let segmentFilter;
  let focusOnFilter;
  if (breakdownVisual?.length) {
    const type = nameFilter(breakdownVisual[0]?.tableName, breakdownVisual[0]?.columnName, [
      breakdownVisual[0]?.type,
    ]);
    const subType = nameFilter(breakdownVisual[0]?.tableName, breakdownVisual[0]?.columnName, [
      breakdownVisual?.[0]?.values?.[0]?.value,
    ]);
    if (isDetailed) {
      if (breakdownVisual?.[0]?.tags?.includes(TAGS.DETAILED)) {
        segmentFilter = type;
      } else {
        segmentFilter = subType;
      }
    } else if (breakdownVisual?.[0]?.tags?.includes(TAGS.DETAILED)) {
      segmentFilter = subType;
    } else {
      segmentFilter = type;
    }
    const focusOnValues = focusOn?.map((item) => item?.label);
    if (focusOnValues.includes('\u200BTotal')) {
      focusOnValues.push('\u200BOverall');
    }
    if (isDetailed && (segmentGroup1?.length || segmentGroup2?.length)) {
      focusOnFilter = focusOnValues?.length
        ? nameFilter(breakdownVisual[0]?.tableName, breakdownVisual[0]?.values[0]?.columnName, [
            ...focusOnValues,
            KEYS.SET_A,
            KEYS.SET_B,
          ])
        : nameFilter(breakdownVisual[0]?.tableName, breakdownVisual[0]?.values[0]?.columnName, [
            KEYS.SET_A,
            KEYS.SET_B,
          ]);
    } else {
      focusOnFilter = focusOnValues?.length
        ? nameFilter(breakdownVisual[0]?.tableName, breakdownVisual[0]?.values[0]?.columnName, [
            ...focusOnValues,
            KEYS.SET_A,
            KEYS.SET_B,
          ])
        : nameFilter(breakdownVisual[0]?.tableName, breakdownVisual[0]?.values[0]?.columnName, [
            'No values selected',
            KEYS.SET_A,
            KEYS.SET_B,
          ]);
    }
  }

  return [segmentFilter, focusOnFilter];
};

/**
 * Creates a Cerulean date filter object for query use.
 *
 * @param {string} filterName - The name of the filter (used as a key in the final object).
 * @param {string | Date} date - The date value to filter by.
 * @param {string} [table=TABLE_MEASURES] - The table name for the target (default is TABLE_MEASURES).
 * @param {string} [columns=COLUMN_DYNAMIC_MEASURE] - The column name for the target (default is COLUMN_DYNAMIC_MEASURE).
 * @param {string} [operator='In'] - The filter operator to use (default is 'In').
 * @returns {Object} A Cerulean date filter object formatted for query use.
 */
export const createCeruleanDateFilter = (
  filterName,
  date,
  table = TABLE_MEASURES,
  columns = COLUMN_DYNAMIC_MEASURE,
  operator = 'In'
) => ({
  [filterName]: {
    target: {
      table,
      columns,
    },
    conditions: {
      operator,
      values: `${format(new Date(date), YEAR_MONTH_DAY_PATTERN)}T00:00:00.000`,
    },
  },
});

/**
 * Creates a Cerulean filter object for query use.
 *
 * @param {string} filterName - The name of the filter (used as a key in the final object).
 * @param {any} values - An any of values to filter by.
 * @param {string} [table=TABLE_MEASURES] - The table name for the target (default is TABLE_MEASURES).
 * @param {string} [columns=COLUMN_DYNAMIC_MEASURE] - The column name for the target (default is COLUMN_DYNAMIC_MEASURE).
 * @param {string} [operator='In'] - The filter operator to use (default is 'In').
 * @returns {Object} A Cerulean filter object formatted for query use.
 */
export const createCeruleanFilter = (
  filterName,
  values,
  table = TABLE_MEASURES,
  columns = COLUMN_DYNAMIC_MEASURE,
  operator = 'In'
) => ({
  [filterName]: {
    target: {
      table,
      columns,
    },
    conditions: {
      operator,
      values,
    },
  },
});
//
export const getBreakdownName = (isDetailed, visualFilters, chartBreakdown) => {
  const breakdownName = visualFilters
    ?.filter((filter) => filter?.id?.includes(chartBreakdown))[0]
    ?.type.replace(/\s+/g, '');
  return BREAKDOWN_MAP[isDetailed ? 'detailed' : 'nonDetailed'][breakdownName];
};
/**
 * Creates a Cerulean breakdown schema filter using provided visual filters and chart breakdown info.
 *
 * @param {Object} params - The parameters for creating the breakdown schema filter.
 * @param {Object[]} params.visualFilters - The list of visual filters, each containing `id` and `type`.
 * @param {string} params.chartBreakdown - The breakdown key to match in visual filters.
 * @param {boolean} params.isDetailed - Flag to indicate whether to use the detailed (RAW) version.
 * @param {string} [params.table=TABLE_MEASURES] - The table name for the target (default is TABLE_MEASURES).
 * @param {string} [params.columns=COLUMN_DYNAMIC_MEASURE] - The column name for the target (default is COLUMN_DYNAMIC_MEASURE).
 * @param {string} [params.operator='In'] - The filter operator to use (default is 'In').
 * @returns {Object} A Cerulean filter object formatted for query use.
 */
export const createCeruleanBreakdownSchemaFilter = ({
  visualFilters,
  chartBreakdown,
  isDetailed,
  table = TABLE_MEASURES,
  columns = COLUMN_DYNAMIC_BREAKDOWN,
  operator = 'In',
}) => {
  const value = getBreakdownName(isDetailed, visualFilters, chartBreakdown);
  //
  return createCeruleanFilter(BREAKDOWN_SCHEMA_FILTER, value, table, columns, operator);
};

/**
 * Builds the trend filters used for fetching time-based tabular data.
 *
 * @param {Object} params - Parameters for filter construction.
 * @param {{ startDate: string, endDate: string }} params.targetDate - Selected target date range for stay.
 * @param {{ startDate: string, endDate: string }} params.bookingDate - Selected booking date range.
 * @param {string|Date} params.targetStartDate - Start date of the target stay period.
 * @param {string|Date} params.targetEndDate - End date of the target stay period.
 * @param {string} params.alignmentToggle - Time alignment type (e.g., 'StayToBooking', 'BookingToStay').
 * @param {boolean} params.symmetricComparisonToggle - Whether to use symmetric comparison for calculated dates.
 * @param {boolean} params.dateComparison - Whether comparison mode is enabled.
 * @param {string} params.currencyType - Currency type used for filtering (e.g., 'USD').
 * @param {string} params.hotelId - Hotel Databricks ID used for filtering.
 * @param {string} params.hotelFocusOn - Comparison Hotel
 * @param {string} params.pms - PMS ID used for filtering.
 * @param {Array<Object>} params.visualFilters - Visual filter definitions, each containing `id` and `type`.
 * @param {string} params.chartBreakdown - Breakdown type (used to extract the filter type from visualFilters).
 * @param {boolean} params.isDetailed - Whether to use detailed (RAW) breakdown value.
 * @param {Array<{ label: string }>} params.focusOn - List of breakdown values selected for filtering.
 *
 * @returns {Object} Filter object compatible with Cerulean API format.
 */

export const buildTrendFilters = ({
  bookingDate,
  targetStartDate,
  targetEndDate,
  alignmentToggle,
  symmetricComparisonToggle,
  dateComparison,
  currencyType,
  hotels,
  hotelFocusOn,
  pms,
  visualFilters,
  chartBreakdown,
  isDetailed,
  focusOn,
  movingAverage,
  isMovingAverage,
  startDate,
  endDate,
  segmentGroupA,
  segmentGroupB,
  segmentGroupAName,
  segmentGroupBName,
  resetBookingDate,
  trendGranularity,
  partialHours,
  lastBookingDate,
  normalization = false,
  originalTargetDate = null,
  isWeeklyAdjusted = false,
}) => {
  const targetDate = {
    startDate: targetStartDate,
    endDate: targetEndDate,
  };
  // For weekly alignment cases, apply the offset between the original selected end date and
  // the custom comparison end date to the adjusted stay-date range
  let customDate = { startDate, endDate };
  if (dateComparison === compareWindows.cus && isWeeklyAdjusted && originalTargetDate?.endDate) {
    customDate = getWeeklyAlignedCustomDate(targetDate, originalTargetDate.endDate, customDate);
  }
  //
  const { adjustedStayDate, adjustedBookingDate } = calculateComparisonDates(
    targetDate,
    bookingDate,
    alignmentToggle,
    symmetricComparisonToggle,
    dateComparison,
    customDate,
    resetBookingDate,
    partialHours,
    lastBookingDate
  );
  const bookingOtb = {
    startDate: new Date(BOOKING_DATE_START_DATE),
    endDate: new Date(),
  };
  const { adjustedBookingDate: adjustedBookingOtbDate } = calculateComparisonDates(
    targetDate,
    bookingOtb,
    alignmentToggle,
    symmetricComparisonToggle,
    dateComparison,
    customDate,
    resetBookingDate,
    partialHours,
    lastBookingDate
  );
  //
  const focusOnLabelList = focusOn?.map((element) => element?.label);
  const hotelIds = !hotels?.hotels
    ? [hotels?.hotelCode]
    : hotels?.hotels?.map((hotel) => hotel?.hotelCode);
  const compHotelIds = !hotelFocusOn?.hotels
    ? [hotelFocusOn?.hotelCode]
    : hotelFocusOn?.hotels?.map((hotel) => hotel?.hotelCode);
  let groupFilters = {};
  if (segmentGroupA && segmentGroupA.length > 0) {
    groupFilters = {
      ...createCeruleanFilter(
        GROUP_A_FILTER,
        segmentGroupA?.map((item) => item?.label)
      ),
      ...createCeruleanFilter(
        GROUP_A_NAME_FILTER,
        segmentGroupAName || GROUP_A_NAME,
        TABLE_MEASURES,
        COLUMN_DYNAMIC_BREAKDOWN
      ),
    };
  }
  if (segmentGroupB && segmentGroupB.length > 0) {
    groupFilters = {
      ...groupFilters,
      ...createCeruleanFilter(
        GROUP_B_FILTER,
        segmentGroupB?.map((item) => item?.label)
      ),
      ...createCeruleanFilter(
        GROUP_B_NAME_FILTER,
        segmentGroupBName || GROUP_B_NAME,
        TABLE_MEASURES,
        COLUMN_DYNAMIC_BREAKDOWN
      ),
    };
  }
  //
  return {
    ...createCeruleanDateFilter(STAY_DATE_LOWER, targetStartDate),
    ...createCeruleanDateFilter(STAY_DATE_UPPER, targetEndDate),
    ...createCeruleanDateFilter(COMPARE_STAY_DATE_LOWER, adjustedStayDate.startDate),
    ...createCeruleanDateFilter(COMPARE_STAY_DATE_UPPER, adjustedStayDate.endDate),
    ...createCeruleanDateFilter(BOOKING_DATE_LOWER, bookingDate.startDate),
    ...createCeruleanDateFilter(BOOKING_DATE_UPPER, bookingDate.endDate),
    ...createCeruleanDateFilter(COMPARE_BOOKING_DATE_LOWER, adjustedBookingDate.startDate),
    ...createCeruleanDateFilter(COMPARE_BOOKING_DATE_UPPER, adjustedBookingDate.endDate),
    ...createCeruleanDateFilter(COMPARE_BOOKING_OTB_DATE_LOWER, adjustedBookingOtbDate.startDate),
    ...createCeruleanDateFilter(COMPARE_BOOKING_OTB_DATE_UPPER, adjustedBookingOtbDate.endDate),
    ...createCeruleanFilter(CURRENCY_FILTER, currencyType),
    ...createCeruleanFilter(HOTEL_FILTER, hotelIds),
    ...createCeruleanFilter(HOTEL_COMP_FILTER, compHotelIds),
    ...createCeruleanFilter(COMPARISON_WINDOW_FILTER, COMPARE_WINDOW_MAP[dateComparison]),
    ...createCeruleanFilter(ALIGNMENT_FILTER, alignmentToggle),
    ...createCeruleanFilter(PMS_FILTER, pms?.toString()),
    ...createCeruleanBreakdownSchemaFilter({ visualFilters, chartBreakdown, isDetailed }),
    ...createCeruleanFilter(BREAKDOWN_LIST_FILTER, focusOnLabelList),
    ...createCeruleanFilter(NORMALIZED_FILTER, normalization),
    ...createCeruleanFilter(GRANULARITY_FILTER, trendGranularity),
    ...groupFilters,
    ...(isMovingAverage
      ? createCeruleanFilter(MOVING_AVERAGE_FILTER, Number(movingAverage?.label))
      : {}),
  };
};
/**
 * Builds the header metadata used in the trend tabular view, including date ranges,
 * comparison periods, PMS sync status, and currency details.
 *
 * @param {Object} params - Parameters used to construct the header.
 * @param {string|Date} params.targetStartDate - Start date of the target stay period.
 * @param {string|Date} params.targetEndDate - End date of the target stay period.
 * @param {string} params.hotelName - Name of the hotel.
 * @param {{ startDate: string|Date, endDate: string|Date }} params.targetDate - The target stay date range.
 * @param {{ startDate: string|Date, endDate: string|Date }} params.bookingDate - The booking date range.
 * @param {boolean} params.alignmentToggle - Whether the date alignment toggle is active.
 * @param {boolean} params.symmetricComparisonToggle - Whether the symmetric comparison is enabled.
 * @param {string} params.currencyType - The selected currency .
 * @param {boolean} [params.aligned=false] - Whether the comparison dates are aligned (defaults to false).
 * @param {boolean} [params.pmsSync=false] - Whether PMS sync is enabled (defaults to false).
 * @param {string} params.dateComparison - The comparison mode.
 *
 * @returns {{
 *   title: string,
 *   hotelName: string,
 *   sections: {
 *     title: string,
 *     value: string
 *   }[]
 * }} An object representing the header data with various labeled values for the tabular chart.
 */
export const buildTrendHeaderData = ({
  targetStartDate,
  targetEndDate,
  hotelName,
  hotelFocusOn = null,
  bookingDate,
  alignmentToggle,
  symmetricComparisonToggle,
  currencyType,
  aligned = false,
  pmsSync = false,
  normalization = false,
  dateComparison,
  startDate,
  endDate,
  isCumulative = false,
  isPercentageChange = false,
  isWeeklyView = false,
  isMovingAverage = false,
  resetBookingDate,
  isHotelComparison = false,
  isComparisonHotelSet = false,
  partialHours,
  lastBookingDate,
  originalTargetDate = null,
}) => {
  const isOtb =
    isSameDay(new Date(bookingDate?.startDate), new Date(resetBookingDate?.startDate)) &&
    isSameDay(new Date(bookingDate?.endDate), new Date(resetBookingDate?.endDate));
  //
  const targetDate = {
    startDate: targetStartDate,
    endDate: targetEndDate,
  };
  // For weekly alignment cases, apply the offset between the original selected end date and
  // the custom comparison end date to the adjusted stay-date range
  const isWeeklyAdjusted = isWeeklyView && !isCumulative;
  let customDate = { startDate, endDate };
  if (dateComparison === compareWindows.cus && isWeeklyAdjusted && originalTargetDate?.endDate) {
    customDate = getWeeklyAlignedCustomDate(targetDate, originalTargetDate.endDate, customDate);
  }

  const { adjustedStayDate, adjustedBookingDate } = calculateComparisonDates(
    targetDate,
    bookingDate,
    alignmentToggle,
    symmetricComparisonToggle,
    dateComparison,
    customDate,
    resetBookingDate,
    partialHours,
    lastBookingDate
  );
  //
  const stayDateRange = `${format(new Date(targetStartDate), YEAR_MONTH_DAY_PATTERN)} to ${format(
    new Date(targetEndDate),
    YEAR_MONTH_DAY_PATTERN
  )}`;

  const stayDateComparison = `${format(
    new Date(adjustedStayDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(adjustedStayDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;
  //
  const bookingDateRange = `${format(
    new Date(bookingDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(bookingDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;
  const bookingDateComparison = `${format(
    new Date(adjustedBookingDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(adjustedBookingDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;
  //
  let comparisonLabel = 'Previous Year - Unaligned';
  if (aligned) {
    switch (dateComparison) {
      case compareWindows.py:
        comparisonLabel = 'Previous Year - Aligned';
        break;
      case compareWindows.pm:
        comparisonLabel = 'Previous Month - Aligned';
        break;
      case compareWindows.pw:
        comparisonLabel = 'Previous Week';
        break;
      case compareWindows.otb:
        comparisonLabel = 'OTB - Aligned';
        break;
      default:
        comparisonLabel = 'Custom - Aligned';
        break;
    }
  } else {
    switch (dateComparison) {
      case compareWindows.py:
        comparisonLabel = 'Previous Year - Unaligned';
        break;
      case compareWindows.pm:
        comparisonLabel = 'Previous Month - Unaligned';
        break;
      case compareWindows.pw:
        comparisonLabel = 'Previous Week';
        break;
      case compareWindows.otb:
        comparisonLabel = 'OTB - Unaligned';
        break;
      default:
        comparisonLabel = 'Custom - Unaligned';
        break;
    }
  }

  const getTitle = () => {
    if (isHotelComparison) {
      if (isCumulative) {
        return TABULAR_HOTEL_CUMULATIVE_TITLE;
      }
      if (isWeeklyView) {
        return TABULAR_HOTEL_WEEKLY_TITLE;
      }
      if (isMovingAverage && isPercentageChange) {
        return TABULAR_HOTEL_PERCENTAGE_TITLE;
      }
      if (isMovingAverage) {
        return TABULAR_HOTEL_MOVING_AVERAGE_TITLE;
      }
      return TABULAR_HOTEL_TITLE;
    }
    if (isCumulative) {
      return TABULAR_CUMULATIVE_TITLE;
    }
    if (isWeeklyView) {
      return TABULAR_WEEKLY_TITLE;
    }
    if (isMovingAverage) {
      if (isPercentageChange) {
        return TABULAR_PERCENTAGE_TITLE;
      }
      return TABULAR_MOVING_AVERAGE_TITLE;
    }
    return TABULAR_TITLE;
  };
  //
  return {
    title: getTitle(),
    hotelName,
    comparisonHotelName: hotelFocusOn,
    isComparisonHotelSet,
    sections: isHotelComparison
      ? [
          { title: STAY_DATE_RANGE, value: stayDateRange, id: 1 },
          { title: BOOKING_DATE_RANGE, value: isOtb ? 'OTB' : bookingDateRange, id: 2 },
          { title: PMS_SYNC, value: pmsSync ? 'On' : 'Off', id: 3 },
          ...(isPercentageChange || isMovingAverage
            ? []
            : [{ title: NORMALIZATION, value: normalization ? 'On' : 'Off', id: 3 }]),
          ...(isPercentageChange ? [] : [{ title: SELECTED_CURRENCY, value: currencyType, id: 4 }]),
        ]
      : [
          { title: STAY_DATE_RANGE, value: stayDateRange, id: 1 },
          { title: STAY_DATE_COMPARISON, value: stayDateComparison, id: 5 },
          { title: BOOKING_DATE_RANGE, value: isOtb ? 'OTB' : bookingDateRange, id: 2 },
          {
            title: BOOKING_DATE_COMPARISON,
            value: !symmetricComparisonToggle ? 'OTB' : `${bookingDateComparison} (STLP)`,
            id: 6,
          },
          { title: SELECTED_CURRENCY, value: currencyType, id: 4 },
          { title: CURRENCY_COMPARISON, value: comparisonLabel, id: 7 },
          ...(!(isWeeklyView || isCumulative || isMovingAverage)
            ? [{ title: PMS_SYNC, value: pmsSync ? 'On' : 'Off', id: 3 }]
            : []),
        ],
  };
};
//
export const getColumns = ({
  isPercentageChange,
  isMovingAverage,
  isCumulative,
  isWeeklyView,
  isForecast,
  isHotelComparison = false,
}) => {
  if (isHotelComparison) {
    if (isForecast && isCumulative) return HOTEL_FORECAST_CUMULATIVE_COLUMNS;
    if (isForecast && isWeeklyView) return HOTEL_WEEKLY_FORECAST_COLUMNS;
    if (isWeeklyView) return HOTEL_WEEKLY_COLUMNS;
    if (isMovingAverage && isPercentageChange) return HOTEL_PERCENTAGE_COLUMNS;
    if (isMovingAverage) return HOTEL_MOVING_AVERAGE_COLUMNS;
    if (isForecast) return HOTEL_W_FORECAST_COLUMNS;
    if (isCumulative) return HOTEL_CUMULATIVE_COLUMNS;
    return HOTEL_COLUMNS;
  }
  if (isForecast && isCumulative) return FORECAST_CUMULATIVE_COLUMNS;
  if (isForecast && isWeeklyView) return WEEKLY_FORECAST_COLUMNS;
  if (isMovingAverage && isPercentageChange) return PERCENTAGE_COLUMNS;
  if (isForecast) return FORECAST_COLUMNS;
  if (isCumulative) return CUMULATIVE_COLUMNS;
  if (isWeeklyView) return WEEKLY_COLUMNS;
  if (isMovingAverage) return MOVING_AVERAGE_COLUMNS;
  return COLUMNS;
};

export const handleCeruleanLogging = ({
  message,
  level = 'info',
  sessionId,
  user,
  eventType = null,
}) => {
  if (ENVIRONMENT.CERULEAN_TABULAR_DEBUG_MODE?.toLowerCase() === 'true') {
    // eslint-disable-next-line no-console
    console.log(message);
  }
  //
  faro.api.setUser({ username: user?.username || 'undefined' });
  faroLog(message, level.toUpperCase(), { sessionId, visualType: eventType });
};

export const getExportFileName = ({
  isCumulative,
  isPercentageChange,
  isMovingAverage,
  isWeeklyView,
}) => {
  let name = FILE_NAMES.DAILY;
  if (isCumulative) {
    name = FILE_NAMES.CUMULATIVE;
  } else if (isPercentageChange) {
    name = FILE_NAMES.PERCENTAGE;
  } else if (isMovingAverage) {
    name = FILE_NAMES.MOVING_AVERAGE;
  } else if (isWeeklyView) {
    name = FILE_NAMES.WEEKLY;
  }
  return name;
};
//
export const getSegmentExportFileName = ({
  isCumulative,
  isPercentageChange,
  isMovingAverage,
  isWeeklyView,
}) => {
  let name = FILE_NAMES.SEGMENT.DAILY;
  if (isWeeklyView) {
    name = FILE_NAMES.SEGMENT.WEEKLY;
  } else if (isPercentageChange) {
    name = FILE_NAMES.SEGMENT.PERCENTAGE;
  } else if (isMovingAverage) {
    name = FILE_NAMES.SEGMENT.MOVING_AVERAGE;
  } else if (isCumulative) {
    name = FILE_NAMES.SEGMENT.CUMULATIVE;
  }
  return name;
};
export const getTimeSegmentExportFileName = ({
  isCumulative,
  isPercentageChange,
  isMovingAverage,
  isWeeklyView,
}) => {
  let name = FILE_NAMES.TIME_SEGMENT.DAILY;
  if (isWeeklyView) {
    name = FILE_NAMES.TIME_SEGMENT.WEEKLY;
  } else if (isPercentageChange) {
    name = FILE_NAMES.TIME_SEGMENT.PERCENTAGE;
  } else if (isMovingAverage) {
    name = FILE_NAMES.TIME_SEGMENT.MOVING_AVERAGE;
  } else if (isCumulative) {
    name = FILE_NAMES.TIME_SEGMENT.CUMULATIVE;
  }
  return name;
};
//
const orderSegmentColumns = (columns) =>
  [...columns].sort((a, b) => {
    const aIsTotal = a.id.startsWith('Total_');
    const bIsTotal = b.id.startsWith('Total_');
    //
    if (aIsTotal && !bIsTotal) return -1;
    if (!aIsTotal && bIsTotal) return 1;
    // Alphabetical order for the rest
    return a.label.localeCompare(b.label);
  });
/**
 * Extracts columns from an object based on a given suffix.
 *
 * @param {Object} data - The source data object containing keys with suffixes.
 * @param {string} suffix - The suffix to filter keys by (e.g. "_room_nights").
 * @returns {Array<{id: string, label: string, type: string, align: string}>}
 *          An array of column definitions, sorted with "Total" first and others alphabetically.
 */
export const getColumnsBySuffix = (data, suffix) =>
  Object.keys(data)
    .filter((key) => key.toLowerCase().endsWith(suffix.toLowerCase()))
    .map((key) => ({
      id: key,
      label: (key.replace(new RegExp(suffix, 'i'), '') || 'Total').trim(),
      type: 'number',
      align: 'right',
    }));
/**
 * Builds a structured column schema for segment-based tabular data.
 *
 * @param {Object} [data={}] - The source data object containing metrics with suffixes.
 * @returns {Array<{id: string, label: string, children: Array<{id: string, label: string, type: string, align: string}>}>}
 *          A schema definition for building tabular columns.
 *
 * @example
 * const obj = {
 *   StayDate: "2025/08/04",
 *   "GDS_room_nights": 3,
 *   "Total_room_nights": 19,
 *   "GDS_total_revenue": 500,
 *   "Total_total_revenue": 1000,
 * };
 *
 * const schema = buildSegmentSchema(obj);
 */
export const buildSegmentSchema = (granularity, data = {}, isDay = false) => [
  {
    id: 'date',
    label: 'Metric',
    children: isDay
      ? [{ id: 'day_name', label: 'Day Name', type: 'string', align: 'left' }]
      : [
          {
            id: 'StayDate',
            label: TREND_GRANULARITY_COLUMN_MAPPER?.[granularity?.toUpperCase()],
            type: 'string',
            align: 'left',
          },
        ],
  },
  {
    id: 'room_nights',
    label: 'Room Nights',
    children: orderSegmentColumns(getColumnsBySuffix(data, '_room_nights')),
  },
  {
    id: 'revenue',
    label: 'Revenue',
    children: orderSegmentColumns(getColumnsBySuffix(data, '_revenue')),
  },
  {
    id: 'adr',
    label: 'ADR',
    children: orderSegmentColumns(getColumnsBySuffix(data, '_ADR')).map((col) =>
      col.label === 'Total' ? { ...col, label: 'Overall' } : col
    ),
  },
  {
    id: 'alos',
    label: 'ALOS',
    children: orderSegmentColumns(getColumnsBySuffix(data, '_ALOS')).map((col) =>
      col.label === 'Total' ? { ...col, label: 'Overall' } : col
    ),
  },
  {
    id: 'lead_time',
    label: 'Lead Time',
    children: orderSegmentColumns(getColumnsBySuffix(data, '_LeadTime')).map((col) =>
      col.label === 'Total' ? { ...col, label: 'Overall' } : col
    ),
  },
  {
    id: 'cancellation',
    label: 'Cancellation Count',
    children: orderSegmentColumns(getColumnsBySuffix(data, '_cancellation')),
  },
];
/**
 * Returns the appropriate segment column configuration based on the selected report options.
 *
 * @param {Object} params - Function parameters.
 * @param {boolean} params.isPercentageChange - Whether percentage change view is enabled.
 * @param {boolean} params.isMovingAverage - Whether moving average view is enabled.
 * @param {boolean} params.isCumulative - Whether cumulative view is enabled.
 * @param {boolean} params.isWeeklyView - Whether weekly view is enabled.
 * @param {boolean} params.isTrendDeAggregate - Whether trend de-aggregate mode is enabled.
 * @param {Array} params.segmentGroupA - First segment group.
 * @param {Array} params.segmentGroupB - Second segment group.
 * @param {boolean} params.isHotelComparison - Whether hotel comparison mode is enabled.
 * @returns {Array} Segment column configuration.
 */
export const getSegmentColumns = ({
  isPercentageChange,
  isMovingAverage,
  isCumulative,
  isWeeklyView,
  isTrendDeAggregate,
  segmentGroupA,
  segmentGroupB,
  isHotelComparison,
}) => {
  const hasGroups = Boolean(segmentGroupA?.length && segmentGroupB?.length);

  /* ---------------- Trend De-aggregate ---------------- */
  if (isTrendDeAggregate) {
    if (isHotelComparison) {
      if (isWeeklyView) return HOTEL_SEGMENT_WEEKLY_COLUMNS;
      if (isCumulative) return HOTEL_SEGMENT_CUMULATIVE_COLUMNS;
      return HOTEL_SEGMENT_DAILY_COLUMNS;
    }

    if (isWeeklyView) return TIME_SEGMENT_WEEKLY_COLUMNS;
    if (isCumulative) return TIME_SEGMENT_CUMULATIVE_COLUMNS;
    return TIME_SEGMENT_DAILY_COLUMNS;
  }

  /* ---------------- Percentage + Moving Avg ---------------- */
  if (isPercentageChange && isMovingAverage) {
    return hasGroups ? SEGMENT_PERCENTAGE_GROUP_WEEKLY_COLUMNS : SEGMENT_PERCENTAGE_WEEKLY_COLUMNS;
  }

  /* ---------------- Cumulative ---------------- */
  if (isCumulative) {
    return hasGroups ? SEGMENT_CUMULATIVE_GROUP_DAILY_COLUMNS : SEGMENT_CUMULATIVE_DAILY_COLUMNS;
  }

  /* ---------------- Weekly ---------------- */
  if (isWeeklyView) {
    return hasGroups ? SEGMENT_GROUP_WEEKLY_COLUMNS : SEGMENT_WEEKLY_COLUMNS;
  }

  /* ---------------- Moving Average ---------------- */
  if (isMovingAverage) {
    return hasGroups
      ? SEGMENT_MOVING_AVERAGE_GROUP_WEEKLY_COLUMNS
      : SEGMENT_MOVING_AVERAGE_WEEKLY_COLUMNS;
  }

  /* ---------------- Daily ---------------- */
  return hasGroups ? SEGMENT_GROUP_DAILY_COLUMNS : SEGMENT_DAILY_COLUMNS;
};
/**
 * Builds header data for the trend segment report.
 *
 * @param {Object} params - Function parameters.
 * @param {string|Date} params.targetStartDate - Start date of the target period.
 * @param {string|Date} params.targetEndDate - End date of the target period.
 * @param {string} params.hotelName - Hotel name.
 * @param {string|Date} params.bookingDate - Booking date.
 * @param {string} params.currencyType - Currency code or type.
 * @param {boolean} [params.pmsSync=false] - Whether PMS sync is enabled.
 * @param {boolean} [params.isCumulative=false] - Whether cumulative data is used.
 * @param {boolean} [params.isPercentageChange=false] - Whether percentage change is displayed.
 * @param {boolean} [params.isWeeklyView=false] - Whether the report is in weekly view.
 * @param {boolean} [params.isMovingAverage=false] - Whether moving average is enabled.
 * @param {boolean} [params.resetBookingDate] - Whether to reset the booking date.
 * @returns {*} Header data for the trend segment report.
 */
export const buildTrendSegmentHeaderData = ({
  targetStartDate,
  targetEndDate,
  hotelName,
  bookingDate,
  currencyType,
  pmsSync = false,
  isCumulative = false,
  isPercentageChange = false,
  isWeeklyView = false,
  isMovingAverage = false,
  resetBookingDate,
}) => {
  const isOtb =
    isSameDay(new Date(bookingDate?.startDate), new Date(resetBookingDate?.startDate)) &&
    isSameDay(new Date(bookingDate?.endDate), new Date(resetBookingDate?.endDate));
  //
  const stayDateRange = `${format(new Date(targetStartDate), YEAR_MONTH_DAY_PATTERN)} to ${format(
    new Date(targetEndDate),
    YEAR_MONTH_DAY_PATTERN
  )}`;
  //
  const bookingDateRange = `${format(
    new Date(bookingDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(bookingDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;
  //
  const getSegmentTitle = () => {
    if (isCumulative) {
      return TABULAR_SEGMENT_CUMULATIVE_TITLE;
    }
    if (isWeeklyView) {
      return TABULAR_SEGMENT_WEEKLY_TITLE;
    }
    if (isPercentageChange) {
      return TABULAR_SEGMENT_PERCENTAGE_TITLE;
    }
    if (isMovingAverage) {
      return TABULAR_SEGMENT_MOVING_AVERAGE_TITLE;
    }
    return TABULAR_SEGMENT_TITLE;
  };
  //
  return {
    title: getSegmentTitle(),
    hotelName,
    sections: isPercentageChange
      ? [
          { title: STAY_DATE_RANGE, value: stayDateRange, id: 1 },
          { title: BOOKING_DATE_RANGE, value: isOtb ? 'OTB' : bookingDateRange, id: 2 },
          { title: PMS_SYNC, value: pmsSync ? 'On' : 'Off', id: 3 },
        ]
      : [
          { title: STAY_DATE_RANGE, value: stayDateRange, id: 1 },
          { title: BOOKING_DATE_RANGE, value: isOtb ? 'OTB' : bookingDateRange, id: 2 },
          { title: PMS_SYNC, value: pmsSync ? 'On' : 'Off', id: 3 },
          { title: SELECTED_CURRENCY, value: currencyType, id: 4 },
        ],
  };
};
/**
 * Removes columns where all detail-row values are zero or null.
 *
 * The row identified by `totalRowValue` is excluded when checking
 * whether a column contains only zero/null values.
 * Columns provided in `exceptColumns` are always preserved.
 *
 * @param {Array<Object>} data - Array of report rows.
 * @param {string} totalRowColumn - Column used to identify the total row.
 * @param {*} totalRowValue - Value that identifies the total row.
 * @param {string[]} exceptColumns - Column names that should never be removed.
 * @returns {Array<Object>} Data with all-zero/null columns removed.
 */
export const removeAllZeroColumns = ({ data, exceptColumns = [] }) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return data;
  }

  const columnsToRemove = Object.keys(data[0] || {}).filter(
    (column) =>
      !exceptColumns.includes(column) &&
      data.every((row) => row[column] === 0 || row[column] === null)
  );

  return data.map((row) =>
    Object.fromEntries(Object.entries(row).filter(([column]) => !columnsToRemove.includes(column)))
  );
};
//
export const groupDataWithParent = (data, key, isWeekly) => {
  if (!Array.isArray(data)) return [];
  // the grand total row is appended at the end and rendered as the aggregation row,
  // so it must not be included as a parent to avoid a duplicate expandable Total row
  const isGrandTotalRow = (row) => row?.StayDate === 'Total' || row?.day_name === 'Total';
  // Separate parent and children
  const parents = data.filter((row) => row?.[key] === 'Total' && !isGrandTotalRow(row));
  if (!parents || !parents?.length) {
    return [];
  }
  const children = data.filter((row) => row?.[key] !== 'Total');
  const total = data.find(isGrandTotalRow);
  const firstKey = Object.keys(parents?.[0])?.[0];
  // Build final output
  const result = parents.map((parent) => {
    // Match children by StayDate
    const matchedChildren = children
      .filter((child) => child[firstKey] === parent[firstKey])
      .map((child) => {
        const newChild = { ...child };
        if (isWeekly) {
          newChild.day_name = child[key];
        } else {
          newChild.StayDate = child[key];
        }

        delete newChild[key];
        return newChild;
      });

    // clean parent
    const newParent = { ...parent };
    delete newParent[key];

    return {
      ...newParent,
      children: matchedChildren,
    };
  });
  result.push(total);
  return result;
};
//
export const buildCombinedVisualHeaderData = ({
  targetStartDate,
  targetEndDate,
  hotelName,
  hotelFocusOn = null,
  bookingDate,
  alignmentToggle,
  symmetricComparisonToggle,
  currencyType,
  pmsSync = false,
  dateComparison,
  startDate,
  endDate,
  isCumulative = false,
  isWeeklyView = false,
  resetBookingDate,
  isHotelComparison = false,
  isComparisonHotelSet = false,
  partialHours,
  lastBookingDate,
  originalTargetDate = null,
}) => {
  const formatRange = (start, end) =>
    `${format(new Date(start), YEAR_MONTH_DAY_PATTERN)} to ${format(
      new Date(end),
      YEAR_MONTH_DAY_PATTERN
    )}`;

  const isOtb =
    isSameDay(new Date(bookingDate?.startDate), new Date(resetBookingDate?.startDate)) &&
    isSameDay(new Date(bookingDate?.endDate), new Date(resetBookingDate?.endDate));

  // For weekly alignment cases, apply the offset between the original selected end date and
  // the custom comparison end date to the adjusted stay-date range
  const isWeeklyAdjusted = isWeeklyView && !isCumulative;
  let customDate = { startDate, endDate };
  if (dateComparison === compareWindows.cus && isWeeklyAdjusted && originalTargetDate?.endDate) {
    customDate = getWeeklyAlignedCustomDate(
      { startDate: targetStartDate, endDate: targetEndDate },
      originalTargetDate.endDate,
      customDate
    );
  }

  /* ---------------- Comparison Dates ---------------- */
  const { adjustedStayDate, adjustedBookingDate } = calculateComparisonDates(
    { startDate: targetStartDate, endDate: targetEndDate },
    bookingDate,
    alignmentToggle,
    symmetricComparisonToggle,
    dateComparison,
    customDate,
    resetBookingDate,
    partialHours,
    lastBookingDate
  );

  /* ---------------- Date Ranges ---------------- */
  const stayDateRange = formatRange(targetStartDate, targetEndDate);
  const stayDateComparison = `${format(
    new Date(adjustedStayDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(adjustedStayDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;

  const bookingDateRange = `${format(
    new Date(bookingDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(bookingDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;
  const bookingDateComparison = `${format(
    new Date(adjustedBookingDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(adjustedBookingDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;

  /* ---------------- Comparison Label ---------------- */
  const comparisonPrefixMap = {
    [compareWindows.py]: 'Previous Year',
    [compareWindows.pm]: 'Previous Month',
    [compareWindows.pw]: 'Previous Week',
    [compareWindows.otb]: 'OTB',
  };

  const comparisonLabel = `${comparisonPrefixMap[dateComparison] || 'Custom'} - ${
    alignmentToggle ? 'Aligned' : 'Unaligned'
  }`;

  /* ---------------- Title ---------------- */
  const getTitle = () => {
    if (isHotelComparison) {
      if (isCumulative) return TABULAR_SEGMENT_TIME_CUMULATIVE_HOTEL_TITLE;
      if (isWeeklyView) return TABULAR_SEGMENT_TIME_WEEKLY_HOTEL_TITLE;
      return TABULAR_SEGMENT_TIME_HOTEL_TITLE;
    }

    if (isCumulative) return TABULAR_SEGMENT_TIME_CUMULATIVE_TITLE;
    if (isWeeklyView) return TABULAR_SEGMENT_TIME_WEEKLY_TITLE;
    return TABULAR_SEGMENT_TIME_TITLE;
  };

  /* ---------------- Sections ---------------- */
  const sections = isHotelComparison
    ? [
        { title: STAY_DATE_RANGE, value: stayDateRange, id: 1 },
        {
          title: BOOKING_DATE_RANGE,
          value: isOtb ? 'OTB' : bookingDateRange,
          id: 2,
        },
        { title: PMS_SYNC, value: pmsSync ? 'On' : 'Off', id: 3 },
        { title: SELECTED_CURRENCY, value: currencyType, id: 4 },
      ]
    : [
        { title: STAY_DATE_RANGE, value: stayDateRange, id: 1 },
        { title: STAY_DATE_COMPARISON, value: stayDateComparison, id: 5 },
        {
          title: BOOKING_DATE_RANGE,
          value: isOtb ? 'OTB' : bookingDateRange,
          id: 2,
        },
        {
          title: BOOKING_DATE_COMPARISON,
          value: symmetricComparisonToggle ? `${bookingDateComparison} (STLP)` : 'OTB',
          id: 6,
        },
        { title: SELECTED_CURRENCY, value: currencyType, id: 4 },
        { title: CURRENCY_COMPARISON, value: comparisonLabel, id: 7 },
      ];

  return {
    title: getTitle(),
    hotelName,
    comparisonHotelName: isHotelComparison ? hotelFocusOn : null,
    isComparisonHotelSet: isHotelComparison && isComparisonHotelSet,
    sections,
  };
};
//
const getColumnsByPrefix = (data, prefix) =>
  Object.keys(data)
    .filter((key) => key.toLowerCase().startsWith(prefix.toLowerCase()))
    .map((key) => {
      const label = (key.replace(new RegExp(prefix, 'i'), '') || 'Total').replaceAll('_', ' ');
      //
      return {
        id: key,
        label: label ? label.charAt(0).toUpperCase() + label.slice(1) : '',
        type: 'number',
        align: 'right',
      };
    });
export const buildSegmentTimeSchema = (granularity, data = {}, isDay = false) => [
  {
    id: 'date',
    label: 'Metric',
    children: isDay
      ? [{ id: 'day_name', label: 'Day Name', type: 'string', align: 'left' }]
      : [
          {
            id: 'StayDate',
            label: TREND_GRANULARITY_COLUMN_MAPPER?.[granularity?.toUpperCase()],
            type: 'string',
            align: 'left',
          },
        ],
  },
  {
    id: 'room_nights',
    label: 'Room Nights',
    children: getColumnsByPrefix(data, 'room_nights_'),
  },
  {
    id: 'revenue',
    label: 'Revenue',
    children: getColumnsByPrefix(data, 'total_revenue_'),
  },
  {
    id: 'adr',
    label: 'ADR',
    children: getColumnsByPrefix(data, 'ADR_'),
  },
  {
    id: 'alos',
    label: 'ALOS',
    children: getColumnsByPrefix(data, 'ALOS_'),
  },
  {
    id: 'lead_time',
    label: 'Lead Time',
    children: getColumnsByPrefix(data, 'LeadTime_'),
  },
  {
    id: 'cancellation',
    label: 'Cancellation Count',
    children: getColumnsByPrefix(data, 'cancellation_'),
  },
];
